import React, { useState } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  AlertCircle, 
  Package, 
  Beaker, 
  Clock,
  CheckCircle,
  XCircle,
  Filter,
  Eye,
  EyeOff
} from 'lucide-react';
import { ScrollArea } from '@smartphone-tycoon/shared-ui';
import { Button } from '@smartphone-tycoon/shared-ui';
import { Card } from '@smartphone-tycoon/shared-ui';
import { ActiveMarketEffects } from '../shared/ActiveMarketEffects';
import type { ActiveEvent } from '@/types/dynamicEvents';
import type { MarketModifier } from '@/types/dynamicEvents';

interface EnhancedEventLogProps {
  events: ActiveEvent[];
  activeModifiers?: Map<string, MarketModifier & { appliedAt: Date; expiresAt?: Date }>;
  onViewDetails?: (event: ActiveEvent) => void;
}

type FilterType = 'all' | 'reactive' | 'systemic' | 'active' | 'resolved';
type CategoryFilter = 'all' | 'economic' | 'technology' | 'competition' | 'regulation' | 'social';

export function EnhancedEventLog({ 
  events, 
  activeModifiers = new Map(),
  onViewDetails 
}: EnhancedEventLogProps) {
  const [filter, setFilter] = useState<FilterType>('all');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [showActiveEffects, setShowActiveEffects] = useState(true);

  const getEventIcon = (event: ActiveEvent) => {
    const { category, severity } = event.event;

    if (event.status === 'resolved') {
      return <CheckCircle className="w-5 h-5 text-theme-success" />;
    }
    if (event.status === 'expired') {
      return <XCircle className="w-5 h-5 text-theme-text-muted" />;
    }
    if (event.status === 'pending') {
      return <Clock className="w-5 h-5 text-theme-warning" />;
    }

    switch (category) {
      case 'economic':
        return severity === 'critical' ? (
          <TrendingDown className="w-5 h-5 text-theme-error" />
        ) : (
          <TrendingUp className="w-5 h-5 text-theme-info" />
        );
      case 'technology':
        return <Beaker className="w-5 h-5 text-theme-accent" />;
      case 'competition':
        return <Package className="w-5 h-5 text-theme-accent" />;
      case 'regulation':
        return <AlertCircle className="w-5 h-5 text-theme-warning" />;
      case 'social':
        return <TrendingUp className="w-5 h-5 text-theme-success" />;
      default:
        return <AlertCircle className="w-5 h-5 text-theme-text-secondary" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'border-theme-error bg-theme-bg-secondary';
      case 'high': return 'border-theme-warning bg-theme-bg-secondary';
      case 'medium': return 'border-theme-warning bg-theme-bg-secondary';
      case 'low': return 'border-theme-accent bg-theme-bg-secondary';
      default: return 'border-theme-border bg-theme-bg-secondary';
    }
  };

  const getStatusBadge = (event: ActiveEvent) => {
    switch (event.status) {
      case 'pending':
        return <span className="px-2 py-1 text-xs bg-theme-bg-tertiary text-theme-text-primary border border-theme-warning rounded-full">Pending</span>;
      case 'active':
        return <span className="px-2 py-1 text-xs bg-theme-bg-tertiary text-theme-text-primary border border-theme-accent rounded-full">Active</span>;
      case 'resolved':
        return <span className="px-2 py-1 text-xs bg-theme-bg-tertiary text-theme-text-primary border border-theme-success rounded-full">Resolved</span>;
      case 'expired':
        return <span className="px-2 py-1 text-xs bg-theme-bg-tertiary text-theme-text-primary rounded-full">Expired</span>;
      default:
        return null;
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatGameDate = (gameDate: { day: number; month: number; year: number; hour?: number; minute?: number }) => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[gameDate.month - 1] || 'Jan';
    const time = gameDate.hour !== undefined && gameDate.minute !== undefined 
      ? ` ${gameDate.hour.toString().padStart(2, '0')}:${gameDate.minute.toString().padStart(2, '0')}`
      : '';
    return `${month} ${gameDate.day}, ${gameDate.year}${time}`;
  };

  const formatModifierEffect = (modifier: MarketModifier) => {
    const field = modifier.field.replace(/([A-Z])/g, ' $1').toLowerCase();
    let effectText = '';
    
    switch (modifier.type) {
      case 'multiplier': {
        const percentage = ((modifier.value - 1) * 100).toFixed(0);
        effectText = `${field}: ${modifier.value > 1 ? '+' : ''}${percentage}%`;
        break;
      }
      case 'additive':
        effectText = `${field}: ${modifier.value > 0 ? '+' : ''}${modifier.value}`;
        break;
      case 'override':
        effectText = `${field}: ${modifier.value}`;
        break;
    }
    
    return effectText;
  };

  const filteredEvents = events.filter(event => {
    if (filter !== 'all') {
      if (filter === 'reactive' && event.event.type !== 'reactive') return false;
      if (filter === 'systemic' && event.event.type !== 'systemic') return false;
      if (filter === 'active' && !['pending', 'active'].includes(event.status)) return false;
      if (filter === 'resolved' && event.status !== 'resolved') return false;
    }
    
    if (categoryFilter !== 'all' && event.event.category !== categoryFilter) {
      return false;
    }
    
    return true;
  });

  const sortedEvents = [...filteredEvents].sort((a, b) => 
    new Date(b.triggeredAt).getTime() - new Date(a.triggeredAt).getTime()
  );

  const activeModifiersList = Array.from(activeModifiers.values());

  return (
    <div className="h-full flex flex-col space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center flex-shrink-0">
        <h2 className="text-xl font-semibold text-theme-text-primary">Event History</h2>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowActiveEffects(!showActiveEffects)}
            className="flex items-center space-x-1"
          >
            {showActiveEffects ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            <span>Effects</span>
          </Button>
          <span className="text-sm text-theme-text-muted">
            {filteredEvents.length} of {events.length} events
          </span>
        </div>
      </div>

      {/* Active Market Effects */}
      {showActiveEffects && activeModifiersList.length > 0 && (
        <ActiveMarketEffects 
          className="flex-shrink-0"
          effects={activeModifiersList.map(modifier => ({
            name: formatModifierEffect(modifier).split(':')[0],
            value: formatModifierEffect(modifier).split(':')[1]?.trim() || '',
            expiration: modifier.expiresAt ? `Until ${formatDate(modifier.expiresAt)}` : ''
          }))}
        />
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 flex-shrink-0">
        <div className="flex items-center space-x-1">
          <Filter className="w-4 h-4 text-theme-text-muted" />
          <span className="text-sm text-theme-text-muted">Type:</span>
          {(['all', 'reactive', 'systemic', 'active', 'resolved'] as FilterType[]).map(f => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f)}
              className="text-xs px-2 py-1"
            >
              {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
            </Button>
          ))}
        </div>
        
        <div className="flex items-center space-x-1">
          <span className="text-sm text-theme-text-muted">Category:</span>
          {(['all', 'economic', 'technology', 'competition', 'regulation', 'social'] as CategoryFilter[]).map(c => (
            <Button
              key={c}
              variant={categoryFilter === c ? 'default' : 'outline'}
              size="sm"
              onClick={() => setCategoryFilter(c)}
              className="text-xs px-2 py-1"
            >
              {c === 'all' ? 'All' : c.charAt(0).toUpperCase() + c.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      {/* Event List */}
      <ScrollArea className="flex-1 min-h-0">
        {sortedEvents.length === 0 ? (
          <div className="text-center py-8 text-theme-text-muted">
            {filter === 'all' ? 'No events recorded yet' : `No ${filter} events found`}
          </div>
        ) : (
          <div className="space-y-3">
            {sortedEvents.map((event, index) => (
              <Card 
                key={`${event.event.id}-${event.triggeredAt.getTime()}-${event.gameMonth}-${event.productContext?.productId || 'global'}-${index}`}
                className={`p-4 ${getSeverityColor(event.event.severity)} border-l-4`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-1">
                    {getEventIcon(event)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center space-x-2">
                        <h4 className="text-sm font-semibold text-theme-text-primary">
                          {event.event.title}
                        </h4>
                        {getStatusBadge(event)}
                        {event.event.historical && (
                          <span className="px-2 py-1 text-xs bg-theme-bg-tertiary text-theme-text-secondary rounded-full">
                            📚 Historical
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-theme-text-muted whitespace-nowrap ml-2">
                        {event.gameDate ? formatGameDate(event.gameDate) : formatDate(event.triggeredAt)}
                      </span>
                    </div>

                    <p className="text-sm text-theme-text-secondary mb-2">
                      {event.event.description}
                    </p>

                    {/* Player Choice Display */}
                    {event.playerChoice && event.event.choices && (
                      <div className="mb-2 p-2 bg-theme-bg-content rounded border">
                        <span className="text-xs font-medium text-theme-text-secondary">Player Choice: </span>
                        <span className="text-xs text-theme-text-primary">
                          {event.event.choices.find(c => c.id === event.playerChoice)?.text}
                        </span>
                      </div>
                    )}

                    {/* Immediate Effects */}
                    {event.outcome?.immediateEffects && (
                      <div className="mb-2">
                        <div className="text-xs font-medium text-theme-text-secondary mb-1">Effects:</div>
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(event.outcome.immediateEffects).map(([key, value]) => {
                            // Skip complex objects and null/undefined values
                            if (typeof value === 'object' || value === null || value === undefined || value === 0) {
                              // Handle special cases for complex objects
                              if (key === 'phaseProgression' && value && typeof value === 'object' && 'targetPhase' in value) {
                                return (
                                  <span
                                    key={key}
                                    className="text-xs px-2 py-1 rounded-full bg-theme-bg-tertiary text-theme-text-primary border border-theme-accent"
                                  >
                                    Phase: {(value as { targetPhase: string }).targetPhase}
                                  </span>
                                );
                              }
                              if (key === 'developmentPhase' && value && typeof value === 'object') {
                                const devPhase = value as Record<string, unknown>;
                                return Object.keys(devPhase).length > 0 ? (
                                  <span
                                    key={key}
                                    className="text-xs px-2 py-1 rounded-full bg-theme-bg-tertiary text-theme-text-primary border border-theme-border"
                                  >
                                    Development: {Object.keys(devPhase).join(', ')}
                                  </span>
                                ) : null;
                              }
                              return null;
                            }
                            
                            // Handle numeric values
                            return (
                              <span
                                key={key}
                                className={`text-xs px-2 py-1 rounded-full bg-theme-bg-tertiary text-theme-text-primary border ${
                                  (value as number) > 0
                                    ? 'border-theme-success'
                                    : 'border-theme-error'
                                }`}
                              >
                                {key}: {(value as number) > 0 ? '+' : ''}{value}
                              </span>
                            );
                          }).filter(Boolean)}
                        </div>
                      </div>
                    )}

                    {/* Market Modifiers */}
                    {event.outcome?.marketModifiers && event.outcome.marketModifiers.length > 0 && (
                      <div className="mb-2">
                        <div className="text-xs font-medium text-theme-text-secondary mb-1">Market Impact:</div>
                        <div className="space-y-1">
                          {event.outcome.marketModifiers.slice(0, 2).map((modifier, index) => (
                            <div key={index} className="text-xs text-theme-text-secondary flex items-center">
                              <span className="w-2 h-2 bg-theme-info rounded-full mr-2"></span>
                              <span>{formatModifierEffect(modifier)}</span>
                              {modifier.duration && (
                                <span className="ml-1 text-theme-text-muted">({modifier.duration}mo)</span>
                              )}
                            </div>
                          ))}
                          {event.outcome.marketModifiers.length > 2 && (
                            <div className="text-xs text-theme-text-muted">
                              +{event.outcome.marketModifiers.length - 2} more effects...
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Meta Info */}
                    <div className="flex items-center justify-between text-xs text-theme-text-muted mt-2 pt-2 border-t border-theme-border">
                      <div className="flex items-center space-x-2">
                        <span className="capitalize">{event.event.category}</span>
                        <span>•</span>
                        <span className="capitalize">{event.event.type}</span>
                        {event.event.region && (
                          <>
                            <span>•</span>
                            <span>{event.event.region}</span>
                          </>
                        )}
                      </div>
                      
                      {onViewDetails && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onViewDetails(event)}
                          className="text-xs h-6 px-2"
                        >
                          Details
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}